import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BRAND, CONTACT_EMAIL } from "../lib/content";
import { CAMPAIGN, campaignCopy } from "../lib/campaign";
import { Seo } from "../components/Seo";
import { FadeUp } from "../components/Reveal";
import { SOCIALS } from "../components/Footer";
import { SiteMenu } from "../components/SiteMenu";

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-flame";

const KickstarterButton = ({ testid }) => (
  <a
    href={CAMPAIGN.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-3 border border-flame bg-flame px-9 py-3.5 font-body text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ember ${FOCUS}`}
  >
    {campaignCopy.cta}
    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
  </a>
);

const youtubeId = (url) => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

const BrandBlock = () => (
  <div data-testid="campaign-brand" className="mx-auto w-fit text-center">
    <Link to="/" aria-label="Tattooed Abroad" data-testid="campaign-logo" className={`block ${FOCUS}`}>
      <img src={BRAND.wordmark} alt="Tattooed Abroad" className="mx-auto h-14 w-auto md:h-[72px]" />
    </Link>
    <div
      role="img"
      aria-label="Journeys Written in Skin."
      data-testid="campaign-tagline"
      className="mx-auto mt-1.5 w-[168px] md:w-[200px]"
      style={{
        aspectRatio: "695 / 130",
        background: "linear-gradient(90deg, #FFD700 0%, #FF8A00 48%, #E2341E 100%)",
        WebkitMaskImage: "url(/brand/journeys-written-in-skin-script-trim.png)",
        maskImage: "url(/brand/journeys-written-in-skin-script-trim.png)",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  </div>
);

const VideoArea = () => {
  const yt = CAMPAIGN.videoUrl ? youtubeId(CAMPAIGN.videoUrl) : null;
  return (
    <section data-testid="campaign-video-section" className="px-5 pt-4 md:pt-5">
      <div
        className="mx-auto"
        style={{ width: "min(840px, 100%, max(280px, calc((100dvh - 360px) * 1.78)))" }}
      >
        {CAMPAIGN.videoUrl ? (
          <div className="relative aspect-video w-full overflow-hidden border border-white/10 bg-black">
            {yt ? (
              <iframe
                src={`https://www.youtube.com/embed/${yt}`}
                title="Tattooed Abroad — Kickstarter campaign video"
                loading="lazy"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 h-full w-full"
              />
            ) : (
              <video
                src={CAMPAIGN.videoUrl}
                poster={CAMPAIGN.videoPoster || undefined}
                controls
                preload="metadata"
                className="absolute inset-0 h-full w-full"
              />
            )}
          </div>
        ) : (
          <div
            data-testid="campaign-video-placeholder"
            className="flex aspect-video w-full items-center justify-center border border-white/10 bg-[#161513]"
          >
            <span className="px-4 text-center font-body text-xs uppercase tracking-[0.25em] text-white/40">
              Kickstarter video coming soon
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

const Invitation = () => (
  <section data-testid="campaign-invitation" className="px-5 pt-5 pb-10 text-center md:pt-6">
    <FadeUp>
      <h1 className="mx-auto max-w-2xl font-display text-2xl font-light leading-snug tracking-tight text-white md:text-4xl">
        Help bring Season One to life.
      </h1>
      <p className="mt-2 font-gothic text-[11px] uppercase tracking-[0.3em] text-flame md:text-xs">
        11 episodes. Saigon, Vietnam.
      </p>
      <p data-testid="campaign-status" className="mt-3 font-body text-sm text-white/80 md:text-base">
        {campaignCopy.status}
      </p>
      <div className="mt-5">
        <KickstarterButton testid="campaign-kickstarter-cta" />
      </div>
      <p data-testid="campaign-note" className="mx-auto mt-3 max-w-md font-body text-xs leading-relaxed text-white/50 md:text-sm">
        {campaignCopy.note}
      </p>
    </FadeUp>
  </section>
);

const SeasonOne = () => (
  <section data-testid="campaign-season-one" className="px-5 pt-16 pb-12 md:pt-24 md:pb-16">
    <div className="mx-auto grid w-full max-w-5xl items-center gap-10 md:grid-cols-2 md:gap-14">
      <FadeUp>
        <h2
          data-testid="campaign-season-one-heading"
          className="font-blackletter text-4xl font-normal leading-tight md:text-5xl"
        >
          <span className="inline-block bg-gradient-to-r from-gold via-[#FF8A00] to-[#E2341E] bg-clip-text text-transparent">
            <span className="block sm:inline">11 Stories.</span>{" "}
            <span className="block sm:inline">1 City.</span>
          </span>
        </h2>
        <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-white/70">
          Every episode follows an artist and client from their first conversation to the finished
          tattoo—exploring what brought them together and the story they’re creating in ink.
        </p>
        <p className="mt-5 max-w-xl border-l-2 border-flame/60 pl-5 font-body text-base leading-relaxed text-white/60">
          I’m Jordy Trachtenberg, a tattoo collector and your host. Tattoos have connected me with
          people and places around the world. Now I want to take you inside those encounters,
          starting here in Saigon.
        </p>
      </FadeUp>
      <FadeUp delay={0.12} className="relative">
        <div className="pointer-events-none absolute -inset-3 bg-flame/10 blur-2xl" />
        <img
          src="/brand/hero-saigon-fists.webp"
          alt="Jordy Trachtenberg holds his tattooed fists toward the camera, the words Sai Gon inked across his knuckles, his face softly blurred behind his hands"
          data-testid="campaign-fists-photo"
          className="relative mx-auto h-auto w-full max-w-sm border border-white/10"
        />
      </FadeUp>
    </div>
  </section>
);

const FinalInvitation = () => (
  <section data-testid="campaign-final" className="px-5 py-12 text-center md:py-14">
    <FadeUp>
      <KickstarterButton testid="campaign-final-kickstarter-cta" />
      <div className="mt-6">
        <Link
          to="/home"
          data-testid="campaign-final-explore"
          className={`group inline-flex items-center gap-3 border border-white/25 px-8 py-4 font-body text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-flame hover:text-flame ${FOCUS}`}
        >
          Explore the website
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </FadeUp>
  </section>
);

const CampaignFooter = () => (
  <footer data-testid="campaign-footer" className="border-t border-white/10 px-5 py-8">
    <div className="mx-auto flex w-full max-w-[960px] flex-col items-center gap-5">
      <div className="flex items-center justify-center gap-3">
        {SOCIALS.map(({ label, href, Icon, external }) => (
          <a
            key={label}
            href={href}
            {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
            aria-label={label}
            data-testid={`campaign-social-${label.toLowerCase()}`}
            className={`flex h-9 w-9 items-center justify-center border border-white/15 text-white/50 transition-colors hover:border-flame hover:text-flame ${FOCUS}`}
          >
            <Icon size={15} />
          </a>
        ))}
      </div>
      <a
        href={`mailto:${CONTACT_EMAIL}`}
        data-testid="campaign-email"
        className={`font-body text-sm text-white/50 transition-colors hover:text-flame ${FOCUS}`}
      >
        {CONTACT_EMAIL}
      </a>
      <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/30">
        © {new Date().getFullYear()} Tattooed Abroad — Journeys Written in Skin
      </p>
    </div>
  </footer>
);

export default function Campaign() {
  return (
    <div className="relative flex min-h-screen flex-col">
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0">
        <img
          src="/brand/purple-smoke.jpg"
          alt=""
          className="animate-smoke h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-ink/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-transparent to-ink" />
      </div>
      <Seo
        title="Tattooed Abroad | Tattoos, Travel & Personal Stories"
        description="Discover Tattooed Abroad, an upcoming documentary series exploring tattoos, travel, and the personal stories connecting artists and clients across cultures."
      />
      <div className="fixed left-3 top-2.5 z-50 md:left-6 md:top-4">
        <SiteMenu />
      </div>
      <div className="relative z-10 flex flex-col md:min-h-[100dvh] md:justify-center">
        <Link
          to="/home"
          data-testid="campaign-explore-link"
          className={`absolute right-4 top-3.5 z-10 font-body text-[10px] uppercase tracking-[0.18em] text-white/60 transition-colors hover:text-flame md:right-8 md:top-6 md:text-xs md:tracking-[0.2em] ${FOCUS}`}
        >
          Explore the website
        </Link>
        <header data-testid="campaign-header" className="px-5 pt-10 md:pt-6">
          <BrandBlock />
        </header>
        <main>
          <VideoArea />
          <Invitation />
        </main>
      </div>
      <div className="relative z-10">
        <SeasonOne />
        <FinalInvitation />
        <CampaignFooter />
      </div>
    </div>
  );
}
